import type { ReactNode } from "react";
import { createBrowserRouter, useRouteError } from "react-router";
import { RouterProvider } from "react-router/dom";

import "./App.scss";
// Hooks
import useDatadogUser from "./hooks/useDatadogUser";
import { useThemeState } from "components/ThemeSelector/useThemeState";
import { useChatWidthState } from "components/ThemeSelector/useChatWidthState";
import { useTaskPolling } from "hooks/tasks/useTaskPolling";
import useGetAccessToken from "components/Login/useGetAccessToken";
// Routes
import ChatLayout from "./routes/ChatLayout";
import NewChat from "./routes/NewChat";
import Chat from "./routes/Chat";
import Experiments from "./routes/Experiments";
import IncognitoChat from "./routes/Incognito";
import DatasetCreator from "./routes/Experiments/DatasetCreator";

// At some point, React Router added its own default error boundary that swallows
// all errors below the RouterProvider. Our app has its own ErrorBoundary of
// last resort that catches uncaught errors. We must workaround ReactRouter's
// default boundary by providing our own that simply rethrows errors.
//
// https://github.com/remix-run/react-router/discussions/10166#discussioncomment-14416588
function RethrowRouteError(): ReactNode {
  throw useRouteError();
}

const router = createBrowserRouter([
  {
    ErrorBoundary: RethrowRouteError,
    children: [
      {
        element: <ChatLayout />,
        children: [
          { path: "/:personaName?", element: <NewChat /> },
          { path: "chats/:chatId", element: <Chat /> },
        ],
      },
      { path: "/incognito", element: <IncognitoChat /> },
      { path: "/experiments", element: <Experiments /> },
      { path: "/experiments/dataset-creator", element: <DatasetCreator /> },
    ],
  },
]);

export default function App() {
  useDatadogUser();
  useThemeState();
  useChatWidthState();
  // Mounted here, above the router, so it lives for the whole session: the
  // poller's timer and budget refs are mount-scoped, and it must keep polling on
  // every route, including those that render no chat surface (ADR 059).
  useTaskPolling(useGetAccessToken());
  return <RouterProvider router={router} />;
}
